import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { createPortal } from "react-dom";
import styled from "styled-components";

import type { IconName } from "@similarweb/icons";
import {
    ScrollArea,
    findParentByClass,
    StructureModal,
    DotsLoader,
    Popover,
    PopoverTrigger,
    PopoverContent,
} from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { AutocompleteStyled } from "app/components/campaigns/searchTrackerFromKeywordsTable/SearchTrackerFromKeywordsTableStyle";
import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";

import { ModalTableSelectionRemoveKeywords } from "../../ModalTableSelectionRemoveKeywords";
import { NO_DATA, proModalStyles } from "../../TableSelectionGroupKeywordConst";
import {
    DropDownItem,
    NoDataWrapper,
    NoResultsWrapper,
    WarningMessageWrapper,
} from "../../TableSelectionGroupKeywordStyle";
import { ETableSelectionNewGroupDropdownMode } from "../../TableSelectionGroupKeywordTypes";
import { TableSelectionKeywordsWarning } from "../../TableSelectionKeywordsWarning";
import { TextfieldStyled } from "../../TextfieldStyled";

const StyledWarningMessageWrapper = styled(WarningMessageWrapper)`
    z-index: 14;
`;

const ScrollAreaWrap = ({ children, ...rest }: { children: ReactNode; [key: string]: unknown }) => {
    return (
        <ScrollArea
            style={{
                maxHeight: 200,
                minHeight: 0,
            }}
            verticalScrollbarStyle={{ borderRadius: 5 }}
            horizontal={false}
            smoothScrolling={true}
            minScrollSize={48}
            {...rest}
        >
            {children}
        </ScrollArea>
    );
};

export interface ICreateOrUpdateKeywordListPopoverProps {
    mode?: ETableSelectionNewGroupDropdownMode;
    count?: number;
    allItemsExistsMessage?: string;
    maxItemsMessage?: string;
    selectedRows: any[];
    onCancel: () => void;

    /* create new list modal props */
    onSubmitNewList?: (groupName: string) => void;
    isCreateNewListModalOpen?: boolean;
    onNewListButtonClick?: () => void;
    newGroupNameLabel: string;
    errorMessage: string;
    isLoading?: boolean;

    /* keyword list/autocomplete props */
    onGroupClick: (item: { text: string; id: string }) => void;
    groups?: { text: string }[];
    groupIconName: string;
    shouldAddNewItemToList?: boolean;
    isKeywordListPopoverOpen: boolean;
    setIsKeywordListPopoverOpen: (isOpen: boolean) => void;
    keywordListButtonRef: React.RefObject<HTMLButtonElement> | null;

    /* keyword removal modal props */
    isKeywordRemovalModalOpen?: boolean;
    setIsKeywordRemovalModalOpen?: (isOpen: boolean) => void;
    onRemoveKeywordsFromList?: () => void;
}

export const CreateOrUpdateKeywordListPopover = ({
    mode = ETableSelectionNewGroupDropdownMode.GROUP_LIST,
    count = 0,
    allItemsExistsMessage = "",
    maxItemsMessage = "",
    shouldAddNewItemToList = true,
    groups = [],
    selectedRows: selectedRowsProp = [],
    onSubmitNewList,
    onCancel: onCancelProp,
    onGroupClick,
    onNewListButtonClick,
    isCreateNewListModalOpen = false,
    isLoading = false,
    newGroupNameLabel = "",
    errorMessage = "",
    groupIconName = "",
    isKeywordListPopoverOpen = false,
    setIsKeywordListPopoverOpen,
    keywordListButtonRef = null,
    isKeywordRemovalModalOpen,
    setIsKeywordRemovalModalOpen,
    onRemoveKeywordsFromList,
}: ICreateOrUpdateKeywordListPopoverProps) => {
    const [groupName, setGroupName] = useState("");
    const [isWarningOpen, setIsWarningOpen] = useState(false);

    useEffect(() => {
        if (
            mode === ETableSelectionNewGroupDropdownMode.ALL_ITEMS_EXISTS ||
            mode === ETableSelectionNewGroupDropdownMode.MAX_ITEMS
        ) {
            setIsWarningOpen(true);
            setIsKeywordListPopoverOpen(false);
        }
    }, [mode, setIsKeywordListPopoverOpen]);

    useEffect(() => {
        document.body.addEventListener("click", handleBodyClick, { capture: true });
        return () => {
            document.body.removeEventListener("click", handleBodyClick, { capture: true });
        };
    }, []);

    // we want the autocomplete and warning messages to close when clicking outside of themselves
    const handleBodyClick = (e: FixmeType) => {
        if (
            !findParentByClass(e.target, "AutocompleteKeywordGroupsAddToList") &&
            !findParentByClass(e.target, "TableSelectionRemoveKeywords") &&
            !findParentByClass(e.target, "TableSelectionKeywordsWarning")
        ) {
            setIsWarningOpen(false);
            setIsKeywordRemovalModalOpen?.(false);
            setIsKeywordListPopoverOpen(false);
        }
    };

    const getAutocompleteData = (query: string) => {
        if (!query) {
            return groups;
        } else {
            const normalizedQuery = query.trim().toLowerCase();
            const filteredGroups = groups.filter((group) => group.text.trim().toLowerCase().includes(normalizedQuery));
            return filteredGroups.length
                ? filteredGroups
                : [NO_DATA, <NoDataWrapper key="no-data">{translate("table.selection.add.no.data")}</NoDataWrapper>];
        }
    };

    const isArrayOfStrings = (array: unknown[]): array is string[] => {
        return array.every((item) => typeof item === "string");
    };

    const selectedRows =
        selectedRowsProp &&
        (isArrayOfStrings(selectedRowsProp)
            ? selectedRowsProp
            : selectedRowsProp.map((item: { keyword: string }) => item.keyword));

    const isDisabled = selectedRows?.length === 0 || !selectedRows;
    const actionButtonsType = isDisabled ? "flat" : "invertedTransparent";

    const getWarningItems = () => {
        // we decide what to display based on the current mode ie. used max items/ items exist in group
        if (mode === ETableSelectionNewGroupDropdownMode.ALL_ITEMS_EXISTS) {
            return [
                <TableSelectionKeywordsWarning
                    key="TableSelectionKeywordsWarning"
                    onCancel={onCancel}
                    message={translate(allItemsExistsMessage)}
                />,
            ];
        }
        return [
            <TableSelectionKeywordsWarning
                key="TableSelectionKeywordsWarning"
                onCancel={onCancel}
                message={translate(maxItemsMessage, {
                    count: count,
                })}
            />,
        ];
    };

    const autocompleteRenderItems = ({ listItems }: FixmeType) => {
        if (!listItems.length) {
            return <NoResultsWrapper>{translate("table.selection.custom.industry.list.no.results")}</NoResultsWrapper>;
        }
        const componentListItems = listItems[0] === NO_DATA ? listItems[1] : listItems.map(itemCreator);

        return <ScrollAreaWrap>{componentListItems}</ScrollAreaWrap>;
    };

    const onItemClick = (item: { text: string; id: string }) => () => {
        allTrackers.dispatchTrackEvent(
            "Button",
            "click",
            `Add to Keyword List/Save/${item.text}/${selectedRows.length}`,
        );
        setIsKeywordListPopoverOpen(false);
        onGroupClick(item);
    };

    const itemCreator = (item: { text: string; id: string }, index: number) => {
        return (
            <DropDownItem
                key={`item-${index}`}
                id={item.id}
                iconName={groupIconName as IconName}
                iconSize="sm"
                onClick={onItemClick(item)}
            >
                {item.text}
            </DropDownItem>
        );
    };

    const onSubmit = () => {
        allTrackers.dispatchTrackEvent(
            "Button",
            "click",
            `Create New Keyword List/Save/${groupName}/${selectedRows.length}`,
        );
        onSubmitNewList?.(groupName);
    };

    const onGroupNameChange = (value: string) => {
        setGroupName(value);
    };

    const onCancel = () => {
        setGroupName("");
        setIsWarningOpen(false);
        setIsKeywordListPopoverOpen(
            mode === ETableSelectionNewGroupDropdownMode.ALL_ITEMS_EXISTS && !isCreateNewListModalOpen,
        );
        onCancelProp();
    };

    return (
        <Flex>
            <Popover
                open={isKeywordListPopoverOpen}
                placement="bottom"
                isClickOutsideEnabled
                onOpenChange={(isOpen) => {
                    if (!isOpen) {
                        setIsKeywordListPopoverOpen(false);
                    }
                }}
            >
                <PopoverTrigger elementRef={keywordListButtonRef} />
                <PopoverContent>
                    <Box
                        position="absolute"
                        top="2px"
                        left="-30px"
                        width="360px"
                        height="300px"
                    >
                        <AutocompleteStyled
                            className="AutocompleteKeywordGroupsAddToList"
                            getListItems={getAutocompleteData}
                            renderItems={autocompleteRenderItems}
                            loadingComponent={<DotsLoader />}
                            autoFocus={true}
                            floating={true}
                            debounce={250}
                            preventTruncateUnlessForced={true}
                            disabled={isDisabled}
                            actionButtonsType={actionButtonsType}
                            placeholder={translate("table.selection.autocomplete.placeholder")}
                            data-automation="add-keyword-autocomplete-with-add-button"
                            shouldAddNewItemToList={shouldAddNewItemToList}
                            onAddNewItemToListClick={onNewListButtonClick}
                            addNewItemToListText={translate("table.selection.create.group")}
                            popupRef={keywordListButtonRef}
                        />
                    </Box>
                </PopoverContent>
            </Popover>
            {isWarningOpen && keywordListButtonRef?.current
                ? createPortal(
                      <StyledWarningMessageWrapper className="TableSelectionKeywordsWarning">
                          {getWarningItems()}
                      </StyledWarningMessageWrapper>,
                      keywordListButtonRef.current,
                  )
                : null}
            <ModalTableSelectionRemoveKeywords
                isOpen={isKeywordRemovalModalOpen ?? false}
                numberOfKeywordsToRemove={count}
                onDelete={onRemoveKeywordsFromList ?? (() => {})}
                onClose={() => {
                    setIsKeywordRemovalModalOpen?.(false);
                }}
            />
            <StructureModal
                isOpen={isCreateNewListModalOpen}
                title={translate("table.selection.newgroup.keywords.create")}
                customStyles={proModalStyles}
                showHeaderDivider={false}
                mainButton={{
                    label: translate("table.selection.newgroup.confirm"),
                    onClick: onSubmit,
                    type: "primary",
                    tooltipEnabled: false,
                    isLoading,
                    isDisabled: groupName.length === 0,
                }}
                secondaryButton={{
                    label: translate("table.selection.newgroup.cancel"),
                    onClick: onCancel,
                    type: "flatDark",
                    tooltipEnabled: false,
                }}
            >
                <Box margin="0 0 24px">
                    <Text
                        height="20px"
                        margin="0 0 16px"
                        fontVariant="body1"
                        as="div"
                    >
                        {translate(newGroupNameLabel)}
                    </Text>
                    <TextfieldStyled
                        error={!!errorMessage}
                        errorMessage={errorMessage}
                        placeholder={translate("table.selection.newgroup.keywords.placeholder")}
                        onChange={onGroupNameChange}
                        dataAutomation="group-name-text-field"
                    />
                </Box>
            </StructureModal>
        </Flex>
    );
};
